/**
 * HistoricalChart - Gráfico Histórico Interativo
 * Mostra evolução de GEB-10, Coágulo e linha de custo ao longo do tempo
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, AlertTriangle } from 'lucide-react';

const HistoricalChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistorical();
  }, []);

  const fetchHistorical = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/market/historical`);
      if (res.ok) {
        const json = await res.json();
        setData(json.historical || []);
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
          <p className="font-bold text-gray-800 mb-1">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }}>
              {entry.name}: R$ {entry.value?.toFixed(2)}/kg
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Identificar meses onde coágulo < COE
  const coeCost = 5.00;

  return (
    <Card data-testid="historical-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-gray-800 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Evolução dos Preços {data.length > 0 ? `(${data[0].month} – ${data[data.length - 1].month})` : ''}
        </CardTitle>
        <p className="text-sm text-gray-500">
          Visualize o paradoxo: GEB-10 sobe, mas coágulo pode cair
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11 }} 
              angle={-45} 
              textAnchor="end" 
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 11 }} 
              tickFormatter={(v) => `R$ ${v}`}
              domain={[0, 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {/* Linha de custo COE */}
            <ReferenceLine 
              y={coeCost} 
              stroke="#ef4444" 
              strokeDasharray="5 5" 
              label={{ value: `COE R$ ${coeCost.toFixed(2)}`, position: 'right', fontSize: 10, fill: '#ef4444' }} 
            />

            <Line
              type="monotone"
              dataKey="geb10"
              name="GEB-10 APABOR"
              stroke="#16a34a"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="coagulo"
              name="Coágulo (Produtor)"
              stroke="#dc2626"
              strokeWidth={2.5}
              dot={{ r: 4 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="preco_importacao"
              name="Preço Importação"
              stroke="#2563eb"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={{ r: 2 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Legenda explicativa */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
            <div className="w-4 h-1 bg-green-600 rounded" />
            <span className="text-xs text-gray-700"><strong>GEB-10:</strong> Referência das usinas</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
            <div className="w-4 h-1 bg-red-600 rounded" />
            <span className="text-xs text-gray-700"><strong>Coágulo:</strong> Preço real ao produtor</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200">
            <AlertTriangle className="w-3 h-3 text-red-500" />
            <span className="text-xs text-gray-700"><strong>Linha vermelha:</strong> Abaixo = prejuízo</span>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-3 text-center">
          Fontes: Notícias Agrícolas (coágulo), APABOR (GEB-10), CNA/IEA (importação). Atualizado conforme dados do administrador.
        </p>
      </CardContent>
    </Card>
  );
};

export default HistoricalChart;
